<template>
  <div class="floating-calculator" :class="{ minimized: !isOpen, 'floating-calculator--outlet': position === 'outlet' }">
    <div class="calculator-header" @click="toggle">
      <div class="title">
        <span>Resumen de precios</span>
      </div>
      <button class="toggle-btn" type="button">
        <span v-if="isOpen">−</span>
        <span v-else>+</span>
      </button>
    </div>
    <div v-if="isOpen" class="calculator-body">
      <div v-if="loading" class="loading">Calculando...</div>
      <div v-else class="pricing-list">
        <div class="pricing-row">
          <span class="label">Total cursos</span>
          <span class="value">$ {{ formatPrice(pricing.precioSubTotal) }}</span>
        </div>
        <div class="pricing-row">
          <span class="label">Desc. sesiones</span>
          <span class="value">- $ {{ formatPrice(pricing.valorDescuentoSesiones) }}</span>
        </div>
        <div class="pricing-row">
          <span class="label">Desc. inscripción online ({{ pricing.porcentajeDescuento }}%)</span>
          <span class="value">- $ {{ formatPrice(pricing.valorDescuento) }}</span>
        </div>
        <div class="pricing-row">
          <span class="label">Matrícula online</span>
          <span class="value">$ {{ formatPrice(pricing.valorMatricula) }}</span>
        </div>
        <div class="divider"></div>
        <div class="pricing-row total">
          <span class="label">Total a pagar</span>
          <span class="value">$ {{ formatPrice(pricing.precioTotal) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import quoteService from '@/services/quoteService';

export default {
  name: 'FloatingPriceCalculator',
  props: {
    programId: { type: [String, Number], default: null },
    locationId: { type: [String, Number], default: null },
    selectedCourses: { type: Array, default: () => [] },
    studentId: { type: [String, Number], default: null },
    orderId: { type: [String, Number], default: null },
    coupon: { type: String, default: null },
    rut: { type: String, default: null },
    cartPacks: { type: Array, default: () => [] },
    student: { type: Object, default: null },
    position: { type: String, default: 'default' }
  },
  data() {
    return {
      quote: null,
      loading: false,
      isOpen: true,
      debounceTimer: null
    }
  },
  computed: {
    outletMode() {
      return this.cartPacks && this.cartPacks.length > 0 && this.student && this.student.id && this.orderId != null;
    },
    coursesIds() {
      if (!this.selectedCourses || this.selectedCourses.length === 0) return '';
      const ids = this.selectedCourses
        .map(c => c.id || c.courseId)
        .filter(Boolean);
      return [...new Set(ids)].join(',');
    },
    pricing() {
      const precioBruto = this.quote?.precioBruto || 0;
      const valorDescuentoSesiones = this.quote?.valorDescuentoSesiones || 0;
      const precioSubTotal = this.quote?.precioSubTotal || 0;
      const valorDescuento = this.quote?.valorDescuento || 0;
      const valorMatricula = this.quote?.valorMatricula || 0;
      const valorCupon = this.quote?.valorCupon || 0;
      const precioTotal = this.quote?.precioTotal || 0;
      
      const porcentajeDescuentoSesiones = precioBruto > 0 ? Math.round((valorDescuentoSesiones / precioBruto) * 100) : 0;
      const porcentajeDescuento = precioSubTotal > 0 ? Math.round((valorDescuento / precioSubTotal) * 100) : 0;
      const totalDescuento = valorDescuentoSesiones + valorDescuento + valorCupon;
      const porcentajeTotalDescuento = (() => {
        const totalDescuentosSinCupon = valorDescuentoSesiones + valorDescuento;
        const porcentaje = precioBruto > 0 ? Math.round((totalDescuentosSinCupon / precioBruto) * 100) : 0;
        return porcentaje;
      })();

      return {
        precioBruto,
        valorDescuentoSesiones,
        precioSubTotal,
        valorDescuento,
        valorMatricula,
        valorCupon,
        precioTotal,
        porcentajeDescuentoSesiones,
        porcentajeDescuento,
        totalDescuento,
        porcentajeTotalDescuento
      };
    }
  },
  watch: {
    programId: 'debouncedFetch',
    locationId: 'debouncedFetch',
    coursesIds: 'debouncedFetch',
    coupon: 'debouncedFetch',
    studentId: 'debouncedFetch',
    orderId: 'debouncedFetch',
    cartPacks: { handler: 'debouncedFetch', deep: true },
    student: { handler: 'debouncedFetch', deep: true }
  },
  mounted() {
    this.fetchQuote();
  },
  methods: {
    normalizeRut(value) {
      if (!value) return null;
      return String(value).replace(/\./g, '');
    },
    toggle() {
      this.isOpen = !this.isOpen;
    },
    formatPrice(price) {
      return Number(price || 0).toLocaleString('es-CL');
    },
    debouncedFetch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchQuote();
      }, 300);
    },
    async fetchQuote() {
      if (this.outletMode) {
        await this.fetchQuoteOutlet();
        return;
      }
      console.log('fetchQuote', this.programId, this.locationId, this.coursesIds);
      if (!this.programId || !this.locationId || !this.coursesIds) {
        this.quote = null;
        return;
      }
      this.loading = true;
      const rut = this.normalizeRut(this.rut || (this.student && this.student.rut));
      try {
        const payload = {
          locationId: this.locationId,
          programId: this.programId,
          coursesIds: this.coursesIds,
          studentId: this.studentId || null,
          orderId: this.orderId || null,
          coupon: this.coupon || null,
          rut: rut
        };
        this.quote = await quoteService.getQuote(payload);
        this.$emit('quote-updated', this.quote);
      } catch (e) {
        this.quote = null;
      } finally {
        this.loading = false;
      }
    },
    async fetchQuoteOutlet() {
      const packs = this.cartPacks;
      const student = this.student;
      const orderId = this.orderId;
      if (!packs.length || !student || !student.id) {
        this.quote = null;
        return;
      }
      const rut = (this.rut || (student.rut && String(student.rut).replace(/\./g, '')) || '');
      this.loading = true;
      try {
        const promises = packs.map((pack) => {
          const locationId = pack.Location?.id || student.selectedLocation?.id;
          const programId = pack.PackDetails?.[0]?.Course?.Program?.id || student.program?.id;
          const coursesIds = (pack.PackDetails || [])
            .map((d) => d.Course?.id)
            .filter(Boolean)
            .join(',');
          if (!locationId || !programId || !coursesIds) return Promise.resolve(null);
          return quoteService.getQuote({
            locationId,
            programId,
            coursesIds,
            studentId: student.id,
            orderId,
            coupon: this.coupon || null,
            rut: this.coupon ? rut : undefined
          }).catch(() => null);
        });
        const responses = await Promise.all(promises);
        const valid = responses.filter(Boolean);
        if (valid.length === 0) {
          this.quote = null;
          return;
        }
        const merged = {
          precioBruto: valid.reduce((s, r) => s + (Number(r.precioBruto) || 0), 0),
          valorDescuentoSesiones: valid.reduce((s, r) => s + (Number(r.valorDescuentoSesiones) || 0), 0),
          precioSubTotal: valid.reduce((s, r) => s + (Number(r.precioSubTotal) || 0), 0),
          valorDescuento: valid.reduce((s, r) => s + (Number(r.valorDescuento) || 0), 0),
          valorMatricula: valid.reduce((s, r) => s + (Number(r.valorMatricula) || 0), 0),
          valorCupon: (valid.find((r) => r.validCupon) || {}).valorDescuentoCupon || 0,
          precioTotal: valid.reduce((s, r) => s + (Number(r.precioTotal) || 0), 0)
        };
        this.quote = merged;
        this.$emit('quote-updated', this.quote);
      } catch (e) {
        this.quote = null;
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.floating-calculator {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 320px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 900;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.floating-calculator.minimized {
  width: 220px;
  height: 44px;
}
.floating-calculator--outlet {
  bottom: auto;
  top: 100px;
  z-index: 999;
}
.floating-calculator--outlet.minimized {
  top: 100px;
}
.calculator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #111111;
  color: #ffffff;
  cursor: pointer;
}
.calculator-header .title {
  font-size: 14px;
  font-weight: 600;
}
.toggle-btn {
  border: none;
  background: transparent;
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.calculator-body {
  padding: 12px;
}
.loading {
  font-size: 13px;
  color: #6b7280;
}
.pricing-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pricing-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #374151;
}
.pricing-row.total {
  font-weight: 700;
  font-size: 14px;
  color: #111111;
}
.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 6px 0;
}
@media (max-width: 480px) {
  .floating-calculator {
    right: 8px;
    left: 8px;
    bottom: 8px;
    width: auto;
  }
}
</style>


